import { IUser } from '@api/models/user.model';
import { CommentIcon, CopyIcon } from '@icon/index';
import EmptyTableTemplate from 'modules/shared/components/EmptyTableTemplate/EmptyTableTemplate';
import useNavName from 'modules/shared/hooks/useNavName';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Tooltip } from '@library/tooltip';
import useAuthPermission from 'modules/shared/hooks/use-auth-permission';
import ProjectAccessDrawerForm from '../ProjectAccessDrawerForm/ProjectAccessDrawerForm';
import UserAddDrawerForm from '../UserAddDrawerForm/UserAddDrawerForm';
import UserDeleteConfirmationDrawer from '../UserDeleteConfirmationDrawer/UserDeleteConfirmationDrawer';
import UserEditDrawerForm from '../UserEditDrawerForm/UserEditDrawerForm';

import { PermissionEnum } from '@api/enum';
import useDialog from '@hooks/use-dialog';
import { Avatar, Button, Table } from '@hyperflake/react-ui-library';
import RoleChip from 'modules/shared/components/RoleChip/RoleChip';
import StatusChip from 'modules/shared/components/StatusChip/StatusChip';
import { PageHeading, PageTemplate } from 'modules/shared/features/content';
import { DeleteShapeIcon, EditIcon } from 'modules/shared/icons';
import useUserListData from 'modules/users/hooks/useUserListData';
import { toast } from 'react-toastify';
import { IClient } from '@api/models';
import { useMutation, useQuery } from '@tanstack/react-query';
import { downloadExcel, getUserTokenToLoginV4 } from '@api/services/user.service';
import { saveAs } from 'file-saver';
import { LoadingButton } from '@library/buttons';

const UserList = () => {
    const navigate = useNavigate();
    const { clientId } = useParams();
    const { clientName } = useNavName(clientId);
    const [searchParams, setSearchParams] = useSearchParams();
    const addUserDailog = useDialog();
    const projectAccessDailog = useDialog<string>();
    const editUserDailog = useDialog<IUser>();
    const deleteUserDailog = useDialog<IUser>();
    const { data, isLoading, addUser, updateUser } = useUserListData(clientId);
    const [loggingInUserId, setLoggingInUserId] = useState<string | null>(null);

    const users = useMemo(() => {
        if (!data) return [];
        return data;
    }, [data]);

    const { pathname } = useLocation();

    useEffect(() => {
        if (Boolean(searchParams.get('showDialog')) === true) {
            if (searchParams.get('userid')?.trim() !== '') {
                // editUserDailog.show(data);
            }
            searchParams.delete('showDialog');
            searchParams.delete('userid');
            setSearchParams(searchParams, {
                replace: true,
            });
        }
    }, [searchParams]);

    const { hasPermission } = useAuthPermission();
    const onUserAdd = (id: string) => {
        projectAccessDailog.show(id);
        addUserDailog.hide();
    };

    const handleCopyDetails = (user: IUser) => {
        let text = `Username:${user.username}\n`;
        text += `Password:${user.password}\n`;
        text += `Sign-in url:${(user.client as IClient).domain}`;

        navigator.clipboard.writeText(text);
        toast.success(`Credentials Copied of ${user.name}`);

        // setHasCopied(true);

        // Change text again agter 3 seconds
        // setTimeout(() => {
        //     setHasCopied(false);
        // }, 2000);
    };

    const { mutate: loginToV4 } = useMutation({
        mutationFn: (userId: string) => getUserTokenToLoginV4(clientId, userId),

        onMutate: (userId) => {
            setLoggingInUserId(userId);
        },

        onSuccess: (token) => {
            if (!token) {
                toast.error('Could not generate login URL');
                return;
            }

            window.open(`${import.meta.env.VITE_APP_REDIRECT_URL}?t=${token}`, '_blank', 'noopener,noreferrer');
        },

        onError: (err: any) => {
            toast.error(err?.message || 'Failed to login');
        },

        onSettled: () => {
            setLoggingInUserId(null);
        },
    });

    const downloadExcelMutation = useMutation(() => downloadExcel(clientId));

    const handleDownloadExcel = async () => {
        try {
            const blob = await downloadExcelMutation.mutateAsync();

            saveAs(blob, `Pulse-${clientName}-UserList.xlsx`);
        } catch (err: any) {
            console.log(err);
            toast.error(err?.data?.message || err?.message || 'Some error occurred, please try again');
        }
    };

    return (
        <PageTemplate>
            <div className="flex justify-between">
                <PageHeading>Users</PageHeading>
                <div className="flex items-center gap-2">
                    <LoadingButton
                        isLoading={downloadExcelMutation.isPending}
                        onClick={handleDownloadExcel}
                        disabled={downloadExcelMutation.isPending}
                        loadingText="Exporting Excel"
                    >
                        Export Excel
                    </LoadingButton>

                    {hasPermission(PermissionEnum.USER_CREATE) && (
                        <Button
                            onClick={() =>
                                // addUserDailog.show()
                                navigate(`/clients/${clientId}/users/add-user`)
                            }
                        >
                            Add New User
                        </Button>
                    )}
                </div>
            </div>

            <div>
                <Table
                    className="mt-6"
                    borderStyle="divider"
                    // hover
                    isLoading={isLoading}
                    data={users}
                    columns={[
                        {
                            header: 'ID',
                        },
                        {
                            header: 'Name',
                        },
                        {
                            header: 'Email',
                        },
                        {
                            header: 'Role',
                        },
                        {
                            header: 'Status',
                        },
                        {
                            header: 'Action',
                        },
                    ]}
                    renderRow={(row) => (
                        <Table.Row
                            key={row._id}
                            onClick={() =>
                                // editUserDailog.show(row)
                                navigate(`/clients/${clientId}/users/${row._id}/edit-user`)
                            }
                        >
                            <Table.Cell>
                                <Link
                                    to={`/clients/${clientId}/users/${row._id}/edit-user`}
                                    // to=""
                                    // /clients/10212/projects/21091/cctv-cameras/98673426/edit-cctv
                                >
                                    <div className="text-primary">{row._id}</div>
                                </Link>
                            </Table.Cell>
                            <Table.Cell>
                                <div className="flex items-center gap-2">
                                    <Avatar src={row?.dpUrl} alt={row?.name} />
                                    {row?.name}
                                </div>
                            </Table.Cell>
                            <Table.Cell>{row.email}</Table.Cell>
                            <Table.Cell>{<RoleChip status={row.role} />}</Table.Cell>
                            <Table.Cell>{<StatusChip status={row.status} />}</Table.Cell>
                            <Table.Cell>
                                <div className="flex items-center gap-4">
                                    <Tooltip placement="top" content="Copy">
                                        <button
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-50"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCopyDetails(row);
                                            }}
                                        >
                                            <CopyIcon />
                                        </button>
                                    </Tooltip>

                                    <Tooltip placement="top" content="Project Access">
                                        <button
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-50"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                projectAccessDailog.show(row._id);
                                            }}
                                        >
                                            <CommentIcon />
                                        </button>
                                    </Tooltip>

                                    {hasPermission(PermissionEnum.USER_DELETE) ? (
                                        <Tooltip placement="top" content="Delete">
                                            <button
                                                className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-50"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    deleteUserDailog.show(row);
                                                }}
                                            >
                                                <DeleteShapeIcon />
                                            </button>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip placement="left" content="Only Admins are authorized to delete User">
                                            <button className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-300 cursor-not-allowed">
                                                <DeleteShapeIcon />
                                            </button>
                                        </Tooltip>
                                    )}
                                    <Tooltip placement="top" content={'Login to V4'}>
                                        <Button
                                            size="xs"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                loginToV4(row._id);
                                            }}
                                            disabled={loggingInUserId === row._id}
                                        >
                                            Login
                                        </Button>
                                    </Tooltip>
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    )}
                />
                {!isLoading && users.length === 0 && <EmptyTableTemplate />}
            </div>

            {/* Add drawer */}
            <UserAddDrawerForm
                open={addUserDailog.isOpen}
                onClose={addUserDailog.hide}
                onSubmit={(id) => onUserAdd(id)}
                onAddUser={addUser}
            />
            {/* edit drawer */}

            <UserEditDrawerForm
                open={editUserDailog.isOpen}
                userId={editUserDailog.data?._id}
                // user={editUserDailog.data}
                onClose={editUserDailog.hide}
                onUserUpdate={(user) => updateUser(user._id, user)}
            />
            {/* Project Access Drawer */}

            <ProjectAccessDrawerForm
                open={projectAccessDailog.isOpen}
                onClose={projectAccessDailog.hide}
                userId={projectAccessDailog.data}
            />

            {/* delete confirmation Dialog */}

            <UserDeleteConfirmationDrawer
                open={deleteUserDailog.isOpen}
                onClose={deleteUserDailog.hide}
                user={deleteUserDailog.data}
            />
        </PageTemplate>
    );
};

export default UserList;
